import type { ChronologyVersion } from "@/components/MedicalChronology/types";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";
import React, { useEffect, useState } from "react";

export interface CompareVersionPickerModalProps {
  open: boolean;
  versions: ChronologyVersion[];
  defaultBaseVersionId: string | null;
  onCompare: (baseVersionId: string, compareVersionId: string) => void;
  onClose: () => void;
}

const SELECT_CLASSES =
  "w-full cursor-pointer rounded-xl border-0 bg-neutral-50 py-3 px-4 text-sm font-medium text-neutral-900 ring-1 ring-neutral-200 transition-colors duration-200 focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50";

export const CompareVersionPickerModal: React.FC<CompareVersionPickerModalProps> = ({
  open,
  versions,
  defaultBaseVersionId,
  onCompare,
  onClose,
}) => {
  const [baseId, setBaseId] = useState("");
  const [compareId, setCompareId] = useState("");

  useEffect(() => {
    if (open) {
      setBaseId(defaultBaseVersionId ?? "");
      setCompareId("");
    } else {
      setBaseId("");
      setCompareId("");
    }
  }, [open, defaultBaseVersionId]);

  const baseCandidates = versions.filter((v) => v.version_id !== compareId);
  const compareCandidates = versions.filter((v) => v.version_id !== baseId);
  const canCompare = Boolean(baseId) && Boolean(compareId) && baseId !== compareId;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="md" hideCloseButton className="gap-0 px-6 py-6 sm:px-8 sm:py-8">
        <DialogClose
          type="button"
          className="absolute right-4 top-4 flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-neutral-500 transition-colors duration-200 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:shadow-focus"
          aria-label="Close"
        >
          <X size={20} strokeWidth={2} aria-hidden />
        </DialogClose>

        <DialogTitle className="pr-10 text-lg font-bold text-neutral-900 sm:text-xl">
          Compare versions
        </DialogTitle>

        <div className="mt-5 sm:mt-6">
          <label
            htmlFor="compare-base-version-select"
            className="mb-1.5 block text-xs font-bold text-neutral-500"
          >
            Base version
          </label>
          <select
            id="compare-base-version-select"
            aria-label="Select the base version"
            value={baseId}
            onChange={(e) => setBaseId(e.target.value)}
            disabled={baseCandidates.length === 0}
            className={SELECT_CLASSES}
          >
            <option value="" disabled>
              Select a version
            </option>
            {baseCandidates.map((version) => (
              <option key={version.version_id} value={version.version_id}>
                {version.version_name}
              </option>
            ))}
          </select>
          {baseCandidates.length === 0 && (
            <p className="mt-2 text-xs text-neutral-500">
              No other versions available to compare against.
            </p>
          )}
        </div>

        <div className="mt-4">
          <label
            htmlFor="compare-target-version-select"
            className="mb-1.5 block text-xs font-bold text-neutral-500"
          >
            Compare version
          </label>
          <select
            id="compare-target-version-select"
            aria-label="Select the version to compare"
            value={compareId}
            onChange={(e) => setCompareId(e.target.value)}
            disabled={compareCandidates.length === 0}
            className={SELECT_CLASSES}
          >
            <option value="" disabled>
              Select a version
            </option>
            {compareCandidates.map((version) => (
              <option key={version.version_id} value={version.version_id}>
                {version.version_name}
              </option>
            ))}
          </select>
          {compareCandidates.length === 0 && (
            <p className="mt-2 text-xs text-neutral-500">
              No other versions available to compare against.
            </p>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:mt-8 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="cursor-pointer rounded-xl border-0 bg-transparent px-5 py-2.5 text-sm font-bold text-neutral-700 ring-1 ring-neutral-200 transition-colors duration-200 hover:bg-neutral-50 hover:ring-neutral-300 focus-visible:shadow-focus"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cursor-pointer rounded-xl border-0 bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors duration-200 hover:bg-primary-hover focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onCompare(baseId, compareId)}
            disabled={!canCompare}
          >
            Compare
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
